import { Notification, Skeleton } from "@r360/library";
import classNames from "classnames";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import useTranslate from "../../hooks/useTranslate";
import "./WtpVideoStream.scss";

type TWtpVideoStream = {
    imageHandler: (arg0: string) => Promise<string | null | undefined>;
    onCardNumberMatch: (arg0: string) => void;
    azureVisionLoading?: boolean;
    onDevicesFound: (arg0: MediaDeviceInfo[], arg1: MediaDeviceInfo | undefined) => void;
};

export type TWtpVideoStreamHandle = {
    startVideo: (preferredDevice: MediaDeviceInfo | undefined) => void;
    stopVideo: () => void;
    pauseVideo: () => void;
    unPauseVideo: () => void;
};

export const WtpVideoStream = forwardRef<TWtpVideoStreamHandle, TWtpVideoStream>(
    ({ imageHandler, onCardNumberMatch, azureVisionLoading, onDevicesFound }: TWtpVideoStream, ref) => {
        const t = useTranslate();
        const videoRef = useRef<HTMLVideoElement>(null);
        const [isVideoRunning, setIsVideoRunning] = useState(false);

        useImperativeHandle(ref, () => ({
            startVideo: (preferredDevice: MediaDeviceInfo | undefined) => startVideo(preferredDevice),
            stopVideo: () => stopVideo(),
            pauseVideo: () => pauseVideo(),
            unPauseVideo: () => unPauseVideo(),
        }));

        useEffect(() => {
            const videoElement = videoRef.current;
            startVideo();

            if (videoElement) {
                videoElement.addEventListener("loadedmetadata", handleLoadMetaData);
            }

            return () => {
                stopVideo();
                if (videoElement) {
                    videoElement.removeEventListener("loadedmetadata", handleLoadMetaData);
                    videoElement.srcObject = null;
                }
            };
            //eslint-disable-next-line
        }, []);

        const startVideo = async (preferredDevice: MediaDeviceInfo | undefined = undefined) => {
            try {
                let stream: MediaStream;

                // If a preferred device is provided, use it.
                if (preferredDevice) {
                    stream = await navigator.mediaDevices.getUserMedia({
                        video: {
                            facingMode: "environment",
                            deviceId: { exact: preferredDevice.deviceId },
                        },
                    });
                } else {
                    stream = await navigator.mediaDevices.getUserMedia({
                        video: { facingMode: "environment" },
                    });
                }

                // Get all devices. This needs to be done after getUserMedia is called.
                const devices = await navigator.mediaDevices.enumerateDevices();
                const filteredDevices = devices.filter(device => device.kind === "videoinput");
                // const lastDevice = filteredDevices[filteredDevices.length - 1];

                // // If no preferred device is provided, use the last device in the list. It's usually the best one.
                // if (!preferredDevice) {
                //     stream = await navigator.mediaDevices.getUserMedia({
                //         video: {
                //             facingMode: "environment",
                //             deviceId: { exact: lastDevice.deviceId },
                //         },
                //     });
                // }

                // Get the used device.
                const videoTrack = stream.getVideoTracks()[0];
                const settings = videoTrack.getSettings();
                const usedDevice = devices.find(d => d.kind === "videoinput" && d.deviceId === settings.deviceId);

                onDevicesFound(filteredDevices, usedDevice);
                setIsVideoRunning(true);

                if (videoRef.current) {
                    videoRef.current.srcObject = null;
                    videoRef.current.srcObject = stream;

                    await new Promise(resolve => {
                        videoRef.current?.addEventListener("loadedmetadata", resolve, {
                            once: true,
                        });
                    });

                    await videoRef.current.play();
                }
            } catch (error) {
                console.error("Error accessing the camera:", error);
            }
        };

        const stopVideo = () => {
            if (videoRef.current && videoRef.current.srcObject instanceof MediaStream) {
                const tracks = videoRef.current.srcObject.getTracks();
                tracks.forEach(track => track.stop());
                setIsVideoRunning(false);
            }
        };

        const pauseVideo = () => {
            if (videoRef.current && videoRef.current instanceof HTMLVideoElement) {
                videoRef.current.pause();
            }
        };

        const unPauseVideo = () => {
            if (videoRef.current && videoRef.current instanceof HTMLVideoElement) {
                videoRef.current.play();
            }
        };

        const handleLoadMetaData = () => {
            captureImage();
        };

        // After Image is captured, send to Tesseract OCR
        const captureImage = async () => {
            try {
                if (!videoRef.current) {
                    console.log("Video element is not available");
                    return;
                }

                const videoWidth = videoRef.current.videoWidth;
                const videoHeight = videoRef.current.videoHeight;

                // Create an offscreen canvas to hold the captured image.
                const canvas = document.createElement("canvas");
                const context = canvas.getContext("2d");

                if (!context) return;
                context.imageSmoothingEnabled = false;

                canvas.width = videoWidth;
                canvas.height = videoHeight;

                // Capture the entire video frame.
                context.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);

                // Calculate the dimensions for the three sections.
                const sectionWidth = videoWidth;
                const topSectionHeight = videoHeight / 3;
                const middleSectionHeight = (videoHeight - topSectionHeight) / 2;

                // Create another offscreen canvas for the original.
                const originalCanvas = document.createElement("canvas");
                const originalContext = originalCanvas.getContext("2d");
                originalCanvas.width = videoWidth;
                originalCanvas.height = videoHeight;

                // Draw the original image onto the original canvas.
                if (!originalContext) return;
                originalContext.drawImage(canvas, 0, 0, videoWidth, videoHeight);

                canvas.width = videoWidth; //! Behövs denna? Width sätts ju redan ovan.
                canvas.height = middleSectionHeight;

                // Draw the middle section of the image onto the final canvas.
                context.drawImage(
                    originalCanvas,
                    0, // X start position for crop.
                    topSectionHeight, // Y start position for crop. At the beginning of the middle section.
                    sectionWidth, // The width of the crop.
                    middleSectionHeight, // The height of the crop, creating a rectangle in the middle.
                    0, // X position to start drawing the crop.
                    0, // Y position to start drawing the crop.
                    sectionWidth, // The width of the crop.
                    middleSectionHeight // The height of the crop.
                );

                const imageData = context.getImageData(0, 0, canvas.width, canvas.height);

                // Make the image grayscale, by setting each color component to the average of the three components.
                for (let i = 0; i < imageData.data.length; i += 4) {
                    const avg = (imageData.data[i] + imageData.data[i + 1] + imageData.data[i + 2]) / 3; // Calculate the grayscale value by averaging the RGB values.
                    // Set each color component to the grayscale value.
                    imageData.data[i] = avg; // Red
                    imageData.data[i + 1] = avg; // Green
                    imageData.data[i + 2] = avg; // Blue
                }

                // Increase the contrast of the image.
                const contrast = 50; // The contrast value to apply to the image.
                const factor = (259 * (contrast + 255)) / (255 * (259 - contrast));

                for (let i = 0; i < imageData.data.length; i += 4) {
                    imageData.data[i] = factor * (imageData.data[i] - 128) + 128; // Red
                    imageData.data[i + 1] = factor * (imageData.data[i + 1] - 128) + 128; // Green
                    imageData.data[i + 2] = factor * (imageData.data[i + 2] - 128) + 128; // Blue
                }

                // Update the canvas with the modified pixel data.
                context.putImageData(imageData, 0, 0);

                // Create a new canvas to resize the image.
                const resizedCanvas = document.createElement("canvas");
                const resizedContext = resizedCanvas.getContext("2d");

                if (!resizedContext) return;
                resizedCanvas.width = canvas.width * 3;
                resizedCanvas.height = canvas.height * 3;

                resizedContext.imageSmoothingEnabled = false;
                resizedContext.drawImage(canvas, 0, 0, resizedCanvas.width, resizedCanvas.height);

                // Convert the final canvas to a data URL.
                const image = resizedCanvas.toDataURL("image/jpeg", 0.5);

                // Get the image data for debugging.
                // console.log(image);

                if (image.length < 10) {
                    console.log(" IMAGE IS BROKEN ", image);
                    return;
                }

                const idNum = await imageHandler(image);

                if (idNum) {
                    stopVideo();
                    onCardNumberMatch(idNum);
                    return;
                }

                captureImage();
            } catch (error) {
                console.error("Error capturing image:", error);
            }
        };

        return (
            <>
                {!isVideoRunning && (
                    <>
                        <Notification type="warning" className="u-mb-18">
                            <p>{t("book.wtp_scanner.searching_for_camera")}</p>
                            <p className="u-mb-0">{t("book.wtp-scanner.allow_camera")}</p>
                        </Notification>
                        <Skeleton height="576px" width="100%" />
                    </>
                )}
                <div
                    className={classNames("wtp-scanner__video-container", {
                        "wtp-scanner__video-container--hidden": !isVideoRunning,
                        "wtp-scanner__video-container--loading": azureVisionLoading,
                    })}
                >
                    <video ref={videoRef} autoPlay playsInline muted width="100%" height="100%"></video>
                    <div className="wtp-scanner__overlay"></div>
                </div>
            </>
        );
    }
);

WtpVideoStream.displayName = "WtpVideoStream";
